import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Phone, Clock, Navigation, ArrowRight } from "lucide-react";
import { useLang, translations } from "@/i18n";
import { getLocation, buildLocationJsonLd } from "@/lib/locations";
import { setSeo } from "@/lib/seo";
import { useEntranceDisabled } from "@/lib/hydrationMotion";
import NotFound from "@/pages/NotFound";

const mapsUrl = (addr) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;

export default function LocalDetalle() {
  const { slug } = useParams();
  const loc = getLocation(slug);
  const { t } = useLang();
  const entranceOff = useEntranceDisabled();

  useEffect(() => {
    if (!loc) return; // invalid slug -> no canonical, no JSON-LD
    const metaDesc = t(loc.desc).replace(/\s+/g, " ").trim();
    setSeo({ title: t(loc.seoTitle), description: metaDesc, path: `/locales/${loc.slug}` });
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "local-detalle-jsonld";
    el.text = JSON.stringify(buildLocationJsonLd(loc));
    document.head.appendChild(el);
    return () => { document.getElementById("local-detalle-jsonld")?.remove(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc, t]);

  if (!loc) return <NotFound />;

  const hasImg = Boolean(loc.image);

  return (
    <div className="pt-[72px]" data-testid={`local-detalle-${loc.slug}`}>
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-10 sm:pt-14 pb-4">
        <nav className="text-sm text-[#5C544F]" aria-label="breadcrumb">
          <Link to="/locales" data-testid="local-detalle-breadcrumb" className="hover:text-[#1A1514] transition-colors">{t(translations.locales.overline)}</Link>
          <span className="mx-2 text-[#B85C38]">/</span>
          <span className="text-[#1A1514]">{loc.name}</span>
        </nav>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-24">
        <motion.div
          initial={entranceOff ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className={`grid gap-0 rounded-sm overflow-hidden border border-[#E3DCD2] bg-[#FBF9F6] ${hasImg ? "lg:grid-cols-2" : "bg-gradient-to-br from-[#F2EFEB] to-[#FBF9F6]"}`}
        >
          {hasImg && (
            <div className="relative min-h-[300px] lg:min-h-[520px]">
              <img src={loc.image} alt={loc.name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async" />
            </div>
          )}
          <div className={`p-8 sm:p-12 lg:p-16 flex flex-col justify-center ${!hasImg ? "lg:py-24 max-w-3xl" : ""}`}>
            <span className="text-[11px] tracking-overline uppercase text-[#B85C38]">{t(loc.area)}</span>
            <h1 data-testid={`local-detalle-title-${loc.slug}`} className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-[#1A1514] mt-3">{loc.name}</h1>
            {t(loc.desc).split(/\n+/).map((s) => s.trim()).filter(Boolean).map((paragraph, idx) => (
              <p key={idx} className={`text-[#5C544F] leading-relaxed ${idx === 0 ? "mt-5" : "mt-4"} ${!hasImg ? "text-lg" : "text-base"}`}>{paragraph}</p>
            ))}

            <div className="mt-7 space-y-3 text-sm text-[#5C544F]">
              <p className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-[#B85C38] shrink-0" /> {loc.addressDisplay}</p>
              {loc.phoneTel && (<p className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#B85C38] shrink-0" /> <a href={`tel:${loc.phoneTel}`} data-testid={`local-detalle-tel-${loc.slug}`} className="hover:text-[#1A1514] transition-colors">{loc.phoneDisplay}</a></p>)}
              <p className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#B85C38] shrink-0" /> {t(loc.hours)}</p>
            </div>

            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/reservas" data-testid={`local-detalle-reservar-${loc.slug}`} className="inline-flex items-center gap-2 bg-[#1A1514] hover:bg-[#3B2A22] text-[#FBF9F6] px-6 py-3 rounded-sm text-sm font-medium transition-colors">
                {t(translations.common.bookTable)} <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={mapsUrl(loc.addressDisplay)} target="_blank" rel="noopener noreferrer" data-testid={`local-detalle-maps-${loc.slug}`} className="inline-flex items-center gap-2 border border-[#1A1514] text-[#1A1514] px-6 py-3 rounded-sm text-sm font-medium hover:bg-[#F2EFEB] transition-colors">
                <Navigation className="w-4 h-4" /> {t(translations.locales.directions)}
              </a>
            </div>
          </div>
        </motion.div>

        <div className="mt-10">
          <Link to="/locales" data-testid="local-detalle-back" className="inline-flex items-center gap-2 text-sm text-[#5C544F] hover:text-[#1A1514] transition-colors">
            ← {t({ es: "Ver todos los locales", en: "See all venues", va: "Veure tots els locals" })}
          </Link>
        </div>
      </section>
    </div>
  );
}
